import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { Page, PermissionAction, PermissionLevel } from "../types/Authorization";

/**
 * Cross-page permission check.
 *
 * `useAbilities(pageName)` only resolves against pages already accepted by the
 * current route's `<PrivateRoute roles>` filter — so it can't answer questions
 * about other pages (e.g. "while viewing a Provider, does this user have access
 * to Organization ratings?"). This hook reads `currentUser.pages` directly,
 * which is the full server-side permission set returned by `GET /users/{id}`.
 *
 * @returns true if the user has the requested action with a level other than No /
 *          Disabled / NotSet, false otherwise. Returns false until currentUser
 *          loads, so render gates are safe by default.
 */
export const usePagePermission = (pageName: string, action: PermissionAction): boolean => {
    const { currentUser } = useUserPermissions();
    if (!currentUser?.pages) return false;

    const page: Page | undefined = currentUser.pages.find(
        (p: Page) => p.name?.toLowerCase() === pageName.toLowerCase(),
    );
    if (!page || !page.permissions) return false;

    const level = page.permissions[action];
    if (typeof level !== "string") return false;
    return (
        level !== PermissionLevel.No &&
        level !== PermissionLevel.Disabled &&
        level !== PermissionLevel.NotSet
    );
};

/**
 * Cross-page CREATE check with the same semantics the Tasks / Activities tabs
 * use for their own "add" buttons: the page must be enabled and `create` must
 * be "yes". Safe by default — false until currentUser loads.
 */
export const useCanCreateOnPage = (pageName: string): boolean => {
    const { currentUser } = useUserPermissions();
    if (!currentUser?.pages) return false;
    const page: Page | undefined = currentUser.pages.find(
        (p: Page) => p.name?.toLowerCase() === pageName.toLowerCase(),
    );
    const permissions: any = page?.permissions;
    if (!permissions || permissions === "not-set") return false;
    return permissions.enabled === true && permissions.create === "yes";
};
